import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DOC_TYPES, data, portal, type Item, type Page } from "@aawaz/api-client";
import { t } from "@aawaz/i18n";
import {
  Badge, Button, Card, Chips, Drawer, EmptyState, ErrorBanner, Icon, MonthPicker, PageHeader, Select, SkeletonRows, Table, fmtDate, fmtPeriod, humanize, sourceLabel,
} from "@aawaz/ui";
import { ItemPanel, useClients } from "../components/ItemPanel";
import { useSession } from "../session";

type StatusChip = "ACCEPTED" | "NEEDS_REVIEW" | "QUARANTINED" | "DUPLICATE" | "ALL";
const STATUS_CHIPS: { value: StatusChip; label: string }[] = [
  { value: "ACCEPTED", label: "Filed" }, { value: "NEEDS_REVIEW", label: "Needs review" }, { value: "DUPLICATE", label: "Duplicates" },
  { value: "QUARANTINED", label: "Held back" }, { value: "ALL", label: "All" },
];

interface ClientDetail { documents_by_period: Record<string, Record<string, number>> }

function fyOf(period: string): string {
  const [y, m] = period.split("-").map(Number) as [number, number];
  const start = m >= 4 ? y : y - 1;
  return `${start}-${String(start + 1).slice(2)}`;
}

/** Screen 3: browse client → FY → month → doc type (the vault tree), filter, preview. */
export function DocumentsPage() {
  const [sp, setSp] = useSearchParams();
  const ca = useSession().has("ca");
  const client = sp.get("client") ?? "";
  const period = sp.get("period") ?? "";
  const docType = sp.get("doc_type") ?? "";
  const status = sp.get("status") ?? "ACCEPTED";
  const source = sp.get("source") ?? "";
  const set = (k: string, v: string) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n, { replace: true }); };
  const [open, setOpen] = useState<string | null>(null);
  const clients = useClients();
  const tree = useQuery({
    enabled: !!client, queryKey: ["clients", client, "tree"],
    queryFn: () => data<ClientDetail>(portal.GET("/portal/v1/clients/{client_id}", { params: { path: { client_id: client } } })),
  });
  const items = useInfiniteQuery({
    queryKey: ["items", "docs", client, period, docType, status, source],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => data<Page<Item>>(portal.GET("/portal/v1/items", { params: { query: {
      limit: 50, ...(client ? { client_org_id: client } : {}), ...(period ? { period } : {}),
      ...(docType ? { doc_type: docType } : {}), ...(status && status !== "ALL" ? { status } : {}), ...(source ? { source } : {}),
      ...(pageParam ? { cursor: pageParam } : {}) } } })),
    getNextPageParam: (p) => p.next_cursor ?? null,
  });
  const byFy = useMemo(() => {
    const out: Record<string, Record<string, Record<string, number>>> = {};
    for (const [per, types] of Object.entries(tree.data?.documents_by_period ?? {})) {
      (out[fyOf(per)] ??= {})[per] = types;
    }
    return out;
  }, [tree.data]);
  const rows = items.data?.pages.flatMap((p) => p.data) ?? [];
  const names = new Map((clients.data?.data ?? []).map((c) => [c.id, c.display_name]));
  const clientName = (id: string | null | undefined) => (id ? names.get(id) ?? "…" : "Unassigned");
  return (
    <>
      <PageHeader title={t("nav.documents")} description={ca
        ? "Everything your clients have sent, organised by client, financial year, month and type."
        : "Every bill, invoice and statement, organised by financial year, month and type."} />
      <div className="ui-toolbar">
        <Chips value={status as StatusChip} onChange={(v) => set("status", v === "ACCEPTED" ? "" : v)} options={STATUS_CHIPS} />
        <div className="ui-toolbar-filters">
          {ca ? <Select aria-label="Client" value={client} onChange={(e) => set("client", e.target.value)}
            options={[{ value: "", label: "All clients" }, ...(clients.data?.data ?? []).map((c) => ({ value: c.id, label: c.display_name }))]} /> : null}
          <Select aria-label="Type" value={docType} onChange={(e) => set("doc_type", e.target.value)}
            options={[{ value: "", label: "Any type" }, ...DOC_TYPES.map((d) => ({ value: d, label: humanize(d) }))]} />
          <Select aria-label="Source" value={source} onChange={(e) => set("source", e.target.value)}
            options={["", "whatsapp", "email_inbound", "gdrive", "onedrive", "tally", "gsp", "consumer_upload", "mcp_upload"].map((v) => ({ value: v, label: v ? sourceLabel(v) : "Any source" }))} />
          <MonthPicker aria-label="Month" value={period} anyLabel="Any month" onChange={(v) => set("period", v)} />
          {client || period || docType || source ? <Button variant="ghost" size="sm" onClick={() => setSp(new URLSearchParams(status !== "ACCEPTED" ? { status } : {}), { replace: true })}>Clear</Button> : null}
        </div>
      </div>
      {/* one column unless the folder tree is shown; a lone child in a two-column grid left the table at half width */}
      <div className={client ? "ui-split" : undefined} style={client ? { gridTemplateColumns: "minmax(0, 260px) minmax(0, 1fr)" } : undefined}>
        {client ? (
          <Card title="Folders" subtitle={clientName(client)}>
            {tree.isLoading ? <SkeletonRows rows={4} /> : Object.keys(byFy).length === 0 ? <p className="ui-hint" style={{ margin: 0 }}>No documents yet.</p> : (
              <nav aria-label="Document tree" className="ui-tree">
                {Object.entries(byFy).sort().reverse().map(([fy, months]) => (
                  <details key={fy} open>
                    <summary><Icon name="folder" size={15} /><strong>FY {fy}</strong></summary>
                    {Object.entries(months).sort().reverse().map(([per, types]) => (
                      <details key={per} open={per === period}>
                        <summary className={per === period && !docType ? "ui-on" : undefined}
                                 onClick={() => { const nsp = new URLSearchParams(sp); nsp.set("period", per); nsp.delete("doc_type"); setSp(nsp, { replace: true }); }}>
                          <Icon name="calendar" size={14} />{fmtPeriod(per)}<span>{Object.values(types).reduce((a, b) => a + b, 0)}</span></summary>
                        {Object.entries(types).map(([dt, n]) => (
                          <button key={dt} className={per === period && dt === docType ? "ui-tree-leaf ui-on" : "ui-tree-leaf"}
                                  onClick={() => { const nsp = new URLSearchParams(sp); nsp.set("period", per); nsp.set("doc_type", dt); setSp(nsp, { replace: true }); }}>
                            {humanize(dt)}<span>{n}</span></button>
                        ))}
                      </details>
                    ))}
                  </details>
                ))}
              </nav>
            )}
          </Card>
        ) : null}
        <Card flush>
          <ErrorBanner error={items.error} />
          {items.isLoading ? <div style={{ padding: 22 }}><SkeletonRows /></div> : rows.length === 0 ? (
            <EmptyState icon="file" title="No documents here">Try another month or type, or clear the filters.</EmptyState>
          ) : (
            <Table rows={rows} rowKey={(r) => r.id} onRowClick={(r) => setOpen(r.id)} columns={[
              { key: "f", header: "Document", render: (r) => (
                <div className="ui-cell-main">
                  <FileIcon item={r} />
                  <div style={{ minWidth: 0 }}>
                    <span className="ui-cell-title">{displayName(r)}</span>
                    <span className="ui-cell-sub">{ca ? clientName(r.client_org_id) : sourceLabel(r.source)}</span>
                  </div>
                </div>) },
              { key: "t", header: "Type", render: (r) => r.doc_type ? humanize(r.doc_type) : <span className="ui-hint">—</span> },
              { key: "p", header: "Month", render: (r) => fmtPeriod(r.period) },
              { key: "s", header: "Status", render: (r) => <Badge value={r.status} /> },
              ...(ca ? [{ key: "o", header: "Source", render: (r: Item) => sourceLabel(r.source) }] : []),
              { key: "d", header: "Received", render: (r) => <span className="ui-hint">{fmtDate(r.received_at)}</span> },
            ]} />
          )}
          {rows.length ? (
            <div className="ui-table-foot">
              <span>{rows.length} document{rows.length === 1 ? "" : "s"} shown</span>
              {items.hasNextPage ? <Button size="sm" onClick={() => void items.fetchNextPage()} busy={items.isFetchingNextPage}>{t("common.more")}</Button> : null}
            </div>
          ) : null}
        </Card>
      </div>
      <Drawer open={!!open} title="Document" onClose={() => setOpen(null)}>
        {open ? <ItemPanel id={open} /> : null}
      </Drawer>
    </>
  );
}

/** Name people recognise: WhatsApp photos arrive without a filename, messages have none at all. */
export function displayName(it: Item): string {
  if (it.filename && !/^\d{10,}$/.test(it.filename)) return it.filename;
  if (it.kind === "message") return "Chat message";
  if (it.kind === "voice") return "Voice note";
  if (it.mime?.startsWith("image/")) return `Photo${it.doc_type ? ` · ${humanize(it.doc_type)}` : ""}`;
  return it.filename ?? humanize(it.kind);
}

export function FileIcon({ item }: { item: Item }) {
  const kind = item.kind === "message" || item.kind === "voice" ? "message"
    : item.mime === "application/pdf" ? "pdf" : item.mime?.startsWith("image/") ? "image" : "other";
  const icon = kind === "message" ? "message" : kind === "image" ? "image" : "file";
  return <span className={`ui-file-icon ui-file-${kind}`} aria-hidden="true"><Icon name={icon} size={18} /></span>;
}
